import React, { useRef, useState } from "react";
import { Routes } from "../../routes";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { BeatLoader } from "react-spinners";
import { connect } from "react-redux";
import {
  bulkDeleteAutoSweep,
  bulkUploadAutoSweep,
  searchAutoSweep,
} from "../../actions/supports";

// ── Constants ─────────────────────────────────────────────────────────────────

const EXPECTED_HEADER = "customer_id,loan_id,msisdn,weekly_cap,daily_cap,transaction_cap,status";
const TEMPLATE_HEADER = "customer_id,loan_id,msisdn,weekly_cap,daily_cap,transaction_cap,status\n";

// ── Helpers ────────────────────────────────────────────────────────────────────

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function validateCsvText(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  const errors = [];

  if (lines.length === 0) {
    return { errors: ["File is empty."], rows: [] };
  }

  const header = lines[0].trim();
  if (header !== EXPECTED_HEADER) {
    return {
      errors: [`Invalid header. Expected: "${EXPECTED_HEADER}" — got: "${header}"`],
      rows: [],
    };
  }

  if (lines.length < 2) {
    return { errors: ["File has no data rows."], rows: [] };
  }

  const seenMsisdns = new Map();
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const rowNum = i + 1;
    const cols = lines[i].split(",");
    const rowErrors = [];

    const [rawCustomerId, rawLoanId, rawMsisdn, rawWeekly, rawDaily, rawTransaction, rawStatus] = cols;

    // Required fields
    if (!rawCustomerId || rawCustomerId.trim() === "")
      rowErrors.push("customer_id is required");
    if (!rawLoanId || rawLoanId.trim() === "")
      rowErrors.push("loan_id is required");
    if (!rawMsisdn || rawMsisdn.trim() === "")
      rowErrors.push("msisdn is required");

    // Numeric checks
    const numFields = [
      ["customer_id", rawCustomerId],
      ["loan_id", rawLoanId],
      ["msisdn", rawMsisdn],
      ["weekly_cap", rawWeekly],
      ["daily_cap", rawDaily],
      ["transaction_cap", rawTransaction],
    ];
    for (const [name, val] of numFields) {
      if (val !== undefined && val.trim() !== "" && isNaN(Number(val.trim()))) {
        rowErrors.push(`${name} must be a number`);
      }
    }

    // Cap values >= 0
    for (const [name, val] of [
      ["weekly_cap", rawWeekly],
      ["daily_cap", rawDaily],
      ["transaction_cap", rawTransaction],
    ]) {
      const n = Number(val?.trim());
      if (!isNaN(n) && n < 0) rowErrors.push(`${name} must be >= 0`);
    }

    // Status 0 or 1 (or empty)
    if (rawStatus !== undefined && rawStatus.trim() !== "") {
      const s = rawStatus.trim();
      if (s !== "0" && s !== "1") rowErrors.push("status must be 0 or 1");
    }

    // Duplicate msisdn within file
    const msisdnVal = rawMsisdn?.trim();
    if (msisdnVal && !isNaN(Number(msisdnVal))) {
      if (seenMsisdns.has(msisdnVal)) {
        rowErrors.push(`Duplicate msisdn (first seen on row ${seenMsisdns.get(msisdnVal)})`);
      } else {
        seenMsisdns.set(msisdnVal, rowNum);
      }
    }

    if (rowErrors.length > 0) {
      errors.push(`Row ${rowNum}: ${rowErrors.join("; ")}`);
    }

    rows.push(cols);
  }

  return { errors, rows };
}

function buildMarkdownReport(report) {
  const header =
    "## Import Summary\n\n" +
    "| # | customer_id | loan_id | msisdn | Status | Reason |\n" +
    "|---|-------------|---------|--------|--------|--------|\n";

  const bodyLines = report.map(
    (r) =>
      `| ${r.rowNumber} | ${r.customerId} | ${r.loanId} | ${r.msisdn ?? "—"} | ${
        r.imported ? "✅ Imported" : "❌ Failed"
      } | ${r.reason} |`
  );

  const total = report.length;
  const imported = report.filter((r) => r.imported).length;
  const failed = total - imported;
  const footer = `\n**Total Rows:** ${total} | **Imported:** ${imported} | **Failed:** ${failed}`;

  return header + bodyLines.join("\n") + footer;
}

// ── Component ─────────────────────────────────────────────────────────────────

const MAX_SEARCH = 10;

const AutoSweep = ({ loading, search, bulkUpload, bulkDelete }) => {
  // Search
  const [searchMsisdn, setSearchMsisdn] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInputError, setSearchInputError] = useState(null);
  const [searchSummary, setSearchSummary] = useState(null);

  // Results table — local state, populated by search or cleared by delete
  const [rows, setRows] = useState([]);

  // Bulk select
  const [selectedMsisdns, setSelectedMsisdns] = useState(new Set());

  // CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [csvErrors, setCsvErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Import report — local state, populated after bulk upload
  const [importReport, setImportReport] = useState(null);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ── Search ──────────────────────────────────────────────────────────────────

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchInputError(null);
    setSearchSummary(null);
    setSelectedMsisdns(new Set());

    const parts = searchMsisdn
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "");

    if (parts.length === 0) return;

    if (parts.length > MAX_SEARCH) {
      setSearchInputError(`You can search up to ${MAX_SEARCH} MSISDNs at a time. You entered ${parts.length}.`);
      return;
    }

    // Single or multiple MSISDNs — normal flow with Redux loading spinner
    setSearchLoading(true);
    const query = parts.join(",");
    const res = await search(query, false); // use silent=false so we get errors
    setSearchLoading(false);

    const found = res?.data || [];
    setRows(found);

    if (parts.length === 1) {
      setSearchSummary(found.length > 0 ? "Found 1 result." : "No profile found for that MSISDN.");
    } else {
      setSearchSummary(`Found ${found.length} of ${parts.length} MSISDNs.`);
    }
  };

  // ── Checkbox selection ──────────────────────────────────────────────────────

  const toggleRow = (msisdn) => {
    setSelectedMsisdns((prev) => {
      const next = new Set(prev);
      next.has(msisdn) ? next.delete(msisdn) : next.add(msisdn);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedMsisdns.size === rows.length) {
      setSelectedMsisdns(new Set());
    } else {
      setSelectedMsisdns(new Set(rows.map((r) => r.msisdn)));
    }
  };

  const allSelected = rows.length > 0 && selectedMsisdns.size === rows.length;

  // ── Bulk delete ─────────────────────────────────────────────────────────────

  const handleBulkDelete = async () => {
    setShowDeleteModal(false);
    const msisdns = Array.from(selectedMsisdns);
    const res = await bulkDelete(msisdns);
    if (res) {
      const deletedSet = new Set(msisdns);
      setRows((prev) => prev.filter((r) => !deletedSet.has(r.msisdn)));
      setSelectedMsisdns(new Set());
    }
  };

  // ── CSV upload ──────────────────────────────────────────────────────────────

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setCsvErrors(["Only .csv files are accepted."]);
      setCsvFile(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const { errors } = validateCsvText(evt.target.result);
      setCsvErrors(errors);
      setCsvFile(errors.length === 0 ? file : null);
    };
    reader.readAsText(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!csvFile || csvErrors.length > 0) return;

    const formData = new FormData();
    formData.append("file", csvFile);
    const res = await bulkUpload(formData);

    if (res) {
      setImportReport(res.data ?? []);
      setCsvFile(null);
      setCsvErrors([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    downloadBlob(TEMPLATE_HEADER, "auto_sweep_profiles_template.csv", "text/csv");
  };

  const downloadReport = () => {
    if (!importReport) return;
    downloadBlob(buildMarkdownReport(importReport), "auto_sweep_import_report.md", "text/markdown");
  };

  // ── Import report summary ───────────────────────────────────────────────────

  const importedCount = importReport ? importReport.filter((r) => r.imported).length : 0;
  const failedCount = importReport ? importReport.length - importedCount : 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-xl-0">
          <Breadcrumb
            className="d-none d-md-inline-block"
            listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}
          >
            <Breadcrumb.Item href={Routes.DashboardOverview.path}>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Auto-Sweep</Breadcrumb.Item>
          </Breadcrumb>
          <h5>Support Issues - Auto-Sweep</h5>
        </div>
      </div>

      {/* ── Card 1: Search & results ─────────────────────────────────────── */}
      <Card border="light" className="table-wrapper shadow-sm mb-4">
        <Card.Body>
          <h6 className="mb-3">Search by MSISDN</h6>
          <Form onSubmit={handleSearch}>
            <Row>
              <Col md={8}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 250781001, 250781002"
                    value={searchMsisdn}
                    onChange={(e) => {
                      setSearchMsisdn(e.target.value);
                      setSearchInputError(null);
                      setSearchSummary(null);
                    }}
                    isInvalid={!!searchInputError}
                    required
                  />
                  <Button type="submit" disabled={loading || searchLoading}>
                    {loading || searchLoading ? <BeatLoader color="#fff" size={8} /> : "Search"}
                  </Button>
                  {searchInputError && (
                    <Form.Control.Feedback type="invalid">
                      {searchInputError}
                    </Form.Control.Feedback>
                  )}
                </InputGroup>
                <Form.Text className="text-muted mt-1 d-block">
                  You can search up to {MAX_SEARCH} MSISDNs at once — separate them with a comma.
                </Form.Text>
              </Col>
            </Row>
          </Form>

          {searchSummary && (
            <p className="mt-2 mb-0 text-muted" style={{ fontSize: "0.85rem" }}>
              {searchSummary}
            </p>
          )}

          {rows.length > 0 && (
            <>
              {selectedMsisdns.size > 0 && (
                <div className="mt-3 d-flex align-items-center gap-2">
                  <span className="text-muted">{selectedMsisdns.size} selected</span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={loading}
                  >
                    Delete Selected
                  </Button>
                </div>
              )}
              <Table responsive className="mt-3 align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    <th>
                      <Form.Check
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>#</th>
                    <th>Customer Name</th>
                    <th>MSISDN</th>
                    <th>Weekly Cap</th>
                    <th>Daily Cap</th>
                    <th>Transaction Cap</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.msisdn}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedMsisdns.has(row.msisdn)}
                          onChange={() => toggleRow(row.msisdn)}
                        />
                      </td>
                      <td>{idx + 1}</td>
                      <td>{row.customerName || "—"}</td>
                      <td>{row.msisdn}</td>
                      <td>{row.weeklyCap}</td>
                      <td>{row.dailyCap}</td>
                      <td>{row.transactionCap}</td>
                      <td>{row.status === 1 ? "Active" : "Inactive"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ── Card 2: CSV Bulk Upload ──────────────────────────────────────── */}
      <Card border="light" className="table-wrapper shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Bulk Upload via CSV</h6>
            <Button variant="outline-secondary" size="sm" onClick={downloadTemplate}>
              Download Template
            </Button>
          </div>

          <Form onSubmit={handleUpload}>
            <Row className="g-3">
              <Col md={8}>
                <Form.Control
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  required
                />
                <Form.Text className="text-muted">
                  Columns: customer_id, loan_id, msisdn, weekly_cap, daily_cap, transaction_cap, status
                </Form.Text>
              </Col>
              <Col md={4} className="d-flex align-items-start">
                <Button
                  type="submit"
                  disabled={loading || !csvFile || csvErrors.length > 0}
                >
                  {loading ? <BeatLoader color="#fff" size={8} /> : "Upload"}
                </Button>
              </Col>
            </Row>
          </Form>

          {csvErrors.length > 0 && (
            <div
              className="mt-3 p-2 border border-danger rounded"
              style={{ maxHeight: 200, overflowY: "auto" }}
            >
              <p className="text-danger fw-bold mb-1">Validation errors — fix before uploading:</p>
              <ul className="text-danger mb-0" style={{ fontSize: "0.85rem" }}>
                {csvErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {importReport && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Import Report</h6>
                <Button variant="outline-secondary" size="sm" onClick={downloadReport}>
                  Download Report
                </Button>
              </div>

              <Table responsive className="align-items-center table-flush" size="sm">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>customer_id</th>
                    <th>loan_id</th>
                    <th>msisdn</th>
                    <th>Status</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {importReport.map((r) => (
                    <tr key={r.rowNumber}>
                      <td>{r.rowNumber}</td>
                      <td>{r.customerId}</td>
                      <td>{r.loanId}</td>
                      <td>{r.msisdn ?? "—"}</td>
                      <td>
                        <span className={r.imported ? "text-success fw-bold" : "text-danger fw-bold"}>
                          {r.imported ? "✅ Imported" : "❌ Failed"}
                        </span>
                      </td>
                      <td>{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <p className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                <strong>Total:</strong> {importReport.length} &nbsp;|&nbsp;
                <strong>Imported:</strong> {importedCount} &nbsp;|&nbsp;
                <strong>Failed:</strong> {failedCount}
              </p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Bulk delete confirmation modal ───────────────────────────────── */}
      <Modal centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You are about to permanently delete{" "}
            <strong>{selectedMsisdns.size} profile(s)</strong>. This cannot be undone.
          </p>
          <ul style={{ fontSize: "0.85rem", maxHeight: 160, overflowY: "auto" }}>
            {Array.from(selectedMsisdns).map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleBulkDelete} disabled={loading}>
            {loading ? <BeatLoader color="#fff" size={8} /> : "Confirm Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const mapStateToProps = (store) => {
  const { loading } = store.supports;
  return { loading };
};

const mapDispatchToProps = (dispatch) => ({
  search: (msisdn, silent) => dispatch(searchAutoSweep(msisdn, silent)),
  bulkUpload: (formData) => dispatch(bulkUploadAutoSweep(formData)),
  bulkDelete: (msisdns) => dispatch(bulkDeleteAutoSweep(msisdns)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AutoSweep);
